/**
 * How often the panel re-reads the show it is looking at (`R-HOST-14`, `R-HOST-31`, §17.4).
 *
 * ## Why the panel re-reads at all
 *
 * `GET /api/shows/{showId}` is *the request the panel polls* — its own note in
 * `packages/functions/src/show.ts` says so. A page that asks for it only on mount and after an
 * action it took itself shows a host watching a lot sell the figures they loaded when they opened
 * it: units left, lots sold out, the allocation ledger and every lot's row, all frozen until they
 * press something. `R-HOST-14` wants the running order
 * *always* visible showing what remains, and §17.4 wants lots sold against lots remaining at a
 * glance; neither survives a page that does not look again.
 *
 * `R-HOST-31` is the other half — two people run one show — and it is the reason a `draft` polls
 * too, slowly: a moderator adding lots to a show while the host builds it should not need the
 * host to press something before they appear.
 *
 * ## Why these numbers
 *
 * A **live** show is the only state where the figures move on their own: every claim and every
 * won round changes a lot's `consumed` without anybody in the panel doing anything. Five seconds
 * is the room's own fallback interval (`roomState.ts`) and is chosen the same way — fast enough
 * that a host reading a number out loud is reading a true one, slow enough to be nothing against
 * a show's traffic. A panel holds a handful of staff, not an audience.
 *
 * Everything before the show is **fifteen seconds**: things change only when a person changes
 * them, and the person is usually the one looking.
 *
 * An **ended or cancelled** show polls not at all. Nothing about it can change except its
 * settlement, which `Settlement.tsx` follows on its own until it comes to rest.
 *
 * ## What this is not
 *
 * It is not the real-time transport. The panel does not subscribe to anything, so a sale reaches
 * the host in up to five seconds rather than in the 500ms spec §11 gives the room — and the room
 * itself is instant, because the *customer* application subscribes. Closing that gap properly is
 * `R-HOST-31`'s remaining half and needs a decision about what a staff channel carries: the
 * show's channel deliberately withholds quantities from viewers (`R-INV-5`, `R-ANL-2`), which
 * are precisely the numbers the panel is asking for, so the panel cannot simply listen to the
 * room's. It is in *Carried forward* in `docs/implementation-plan.md`.
 */

/** While the show is running and lots are selling. The room's fallback interval, for its reasons. */
export const LIVE_POLL_MS = 5_000;

/** While it is being built or is waiting to start. Nothing moves unless somebody moves it. */
export const IDLE_POLL_MS = 15_000;

/**
 * The interval for a show in this state, or `undefined` where there is nothing left to watch.
 *
 * `undefined` rather than a very long interval, so the caller's effect can decide not to hold a
 * timer at all — a panel left open on last week's show should be doing nothing.
 */
export function panelPollInterval(state: string | undefined): number | undefined {
  if (state === "ended" || state === "cancelled") return undefined;
  return state === "live" ? LIVE_POLL_MS : IDLE_POLL_MS;
}
